/**
 * Oracle DDL Generators
 *
 * Pure frontend string-templating functions for generating Oracle DDL
 * statements from form field values. Used by CREATE/ALTER dialogs.
 */

// ---- Create Sequence ----

export type CreateSequenceParams = {
  schema: string;
  name: string;
  startWith: string;
  incrementBy: string;
  minValue: string;
  maxValue: string;
  cacheSize: string;
  cycle: boolean;
  order: boolean;
};

export function generateCreateSequenceDDL(p: CreateSequenceParams): string {
  const parts: string[] = [`CREATE SEQUENCE "${p.schema}"."${p.name}"`];

  if (p.startWith) parts.push(`  START WITH ${p.startWith}`);
  if (p.incrementBy) parts.push(`  INCREMENT BY ${p.incrementBy}`);
  if (p.minValue) parts.push(`  MINVALUE ${p.minValue}`);
  else parts.push('  NOMINVALUE');
  if (p.maxValue) parts.push(`  MAXVALUE ${p.maxValue}`);
  else parts.push('  NOMAXVALUE');
  if (p.cacheSize && Number(p.cacheSize) > 0) parts.push(`  CACHE ${p.cacheSize}`);
  else parts.push('  NOCACHE');
  parts.push(p.cycle ? '  CYCLE' : '  NOCYCLE');
  parts.push(p.order ? '  ORDER' : '  NOORDER');

  return parts.join('\n') + ';';
}

// ---- Create Synonym ----

export type CreateSynonymParams = {
  schema: string;
  name: string;
  targetSchema: string;
  targetObject: string;
  isPublic: boolean;
};

export function generateCreateSynonymDDL(p: CreateSynonymParams): string {
  const prefix = p.isPublic ? 'CREATE PUBLIC SYNONYM' : 'CREATE SYNONYM';
  const synonymName = p.isPublic ? `"${p.name}"` : `"${p.schema}"."${p.name}"`;
  const target = p.targetSchema ? `"${p.targetSchema}"."${p.targetObject}"` : `"${p.targetObject}"`;
  return `${prefix} ${synonymName}\n  FOR ${target};`;
}

// ---- Create DB Link ----

export type CreateDbLinkParams = {
  name: string;
  connectString: string;
  username: string;
  password: string;
  isPublic: boolean;
};

export function generateCreateDbLinkDDL(p: CreateDbLinkParams): string {
  const prefix = p.isPublic ? 'CREATE PUBLIC DATABASE LINK' : 'CREATE DATABASE LINK';
  const parts: string[] = [`${prefix} "${p.name}"`];

  if (p.username) {
    parts.push(`  CONNECT TO "${p.username}" IDENTIFIED BY "${p.password}"`);
  }
  if (p.connectString) {
    parts.push(`  USING '${p.connectString}'`);
  }

  return parts.join('\n') + ';';
}

// ---- Alter Sequence ----

export type AlterSequenceParams = {
  schema: string;
  name: string;
  incrementBy: string;
  minValue: string;
  maxValue: string;
  cacheSize: string;
  cycle: boolean;
  order: boolean;
};

export function generateAlterSequenceDDL(p: AlterSequenceParams): string {
  const parts: string[] = [`ALTER SEQUENCE "${p.schema}"."${p.name}"`];

  if (p.incrementBy) parts.push(`  INCREMENT BY ${p.incrementBy}`);
  if (p.minValue) parts.push(`  MINVALUE ${p.minValue}`);
  else parts.push('  NOMINVALUE');
  if (p.maxValue) parts.push(`  MAXVALUE ${p.maxValue}`);
  else parts.push('  NOMAXVALUE');
  if (p.cacheSize && Number(p.cacheSize) > 0) parts.push(`  CACHE ${p.cacheSize}`);
  else parts.push('  NOCACHE');
  parts.push(p.cycle ? '  CYCLE' : '  NOCYCLE');
  parts.push(p.order ? '  ORDER' : '  NOORDER');

  return parts.join('\n') + ';';
}
